import type React from "react";
import { tv, type VariantProps } from "tailwind-variants";

import { focusRing } from "@/components/ui/utils";

export const badgeStyles = tv({
  extend: focusRing,
  base: "inline-flex justify-center items-center gap-x-1.5 py-0.5 text-xs/5 ring-1 ring-white/10 font-medium dark:ring-inset forced-colors:outline data-[slot=icon]:**:size-3",
  variants: {
    color: {
      primary: "bg-primary text-primary-foreground",
      blue: "bg-blue-100 text-blue-700 dark:bg-blue-400/10 dark:text-blue-400",
      gray: "bg-muted text-muted-foreground",
      green:
        "bg-green-100 text-green-700 dark:bg-green-400/10 dark:text-green-400",
      red: "bg-red-100 text-red-700 dark:bg-red-400/10 dark:text-red-400",
      orange:
        "bg-orange-100 text-orange-700 dark:bg-orange-400/10 dark:text-orange-400",
      amber:
        "bg-amber-100 text-amber-700 dark:bg-amber-400/10 dark:text-amber-400",
      yellow:
        "bg-yellow-100 text-yellow-800 dark:bg-yellow-400/10 dark:text-yellow-400",
      indigo:
        "bg-indigo-100 text-indigo-700 dark:bg-indigo-400/10 dark:text-indigo-400",
    },
    shape: {
      square: "rounded px-1.5",
      circle: "px-2 rounded-full",
    },
    size: {
      sm: "text-2xs h-5 px-[calc(--spacing(2)-2px)]",
      md: "text-xs h-6 py-1 px-[calc(--spacing(2)-0px)]",
      lg: "text-sm h-7 py-1 px-[calc(--spacing(3)-2px)]",
    },
    variant: {
      soft: "",
      outline: "border border-border text-foreground bg-transparent",
    },
  },

  defaultVariants: {
    color: "gray",
    shape: "square",
    size: "md",
    variant: "soft",
  },
});

type BadgeProps = React.ComponentProps<"span"> &
  VariantProps<typeof badgeStyles>;

export function Badge({
  children,
  color,
  shape,
  size,
  className,
  variant,
  ...props
}: BadgeProps) {
  return (
    <span
      {...props}
      className={badgeStyles({ color, shape, size, variant, className })}
    >
      {children}
    </span>
  );
}
